import {
  ProcessedProvider,
  normalizeProviderSchemaToJsonString,
} from "../helpers";
import { PaymentMethod, MethodId, ProviderCode, CardBrand } from "../enums";

export interface LocalPaymentFormMethod {
  id: string;
  order: number;
  description: string;
  enabled: boolean;
  asksFor: string;
  brands: Array<{
    id: string;
    description: string;
    enabled: boolean;
    imageUrl: string;
  }>;
}

export interface LocalPaymentCatalog {
  methodOrder: string[];
  creditBrandOptions: string[];
  debitBrandOptions: string[];
}

const METHOD_API_TO_PT: Record<string, string> = {
  Pix: "Pix",
  Cash: "Dinheiro",
  Debit: "Cartão de Débito",
  Credit: "Cartão de Crédito",
};

const DEFAULT_METHOD_ORDER: string[] = [
  PaymentMethod.Credit,
  PaymentMethod.Debit,
  PaymentMethod.Pix,
  PaymentMethod.Cash,
];

const DEFAULT_CARD_BRANDS: string[] = [
  CardBrand.Mastercard,
  CardBrand.Visa,
  CardBrand.Hipercard,
  CardBrand.Elo,
  CardBrand.Amex,
];

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function enumStringsFromItemsNode(itemsNode: unknown): string[] {
  const itemsObj = asRecord(itemsNode);
  if (!itemsObj) return [];
  if (Array.isArray(itemsObj.enum)) {
    return itemsObj.enum.filter((x): x is string => typeof x === "string");
  }
  return [];
}

function extractMethodsFromSchema(root: Record<string, unknown>): string[] {
  const props = asRecord(root.properties);
  if (!props) return [];
  const methodsNode = asRecord(props.methods);
  if (!methodsNode) return [];
  const fromItems = enumStringsFromItemsNode(methodsNode.items);
  if (fromItems.length > 0) return fromItems;
  return [];
}

function extractCreditDebitBrandEnumsFromSchema(
  root: Record<string, unknown>,
): { creditBrandOptions: string[]; debitBrandOptions: string[] } {
  const props = asRecord(root.properties);
  if (!props) {
    return { creditBrandOptions: [], debitBrandOptions: [] };
  }
  const acquirer = asRecord(props.acquirer);
  if (!acquirer) {
    return { creditBrandOptions: [], debitBrandOptions: [] };
  }
  const acquirerProps = asRecord(acquirer.properties);
  if (!acquirerProps) {
    return { creditBrandOptions: [], debitBrandOptions: [] };
  }
  const creditBrandsNode = asRecord(acquirerProps.creditBrands);
  const debitBrandsNode = asRecord(acquirerProps.debitBrands);
  return {
    creditBrandOptions: creditBrandsNode
      ? enumStringsFromItemsNode(creditBrandsNode.items)
      : [],
    debitBrandOptions: debitBrandsNode
      ? enumStringsFromItemsNode(debitBrandsNode.items)
      : [],
  };
}

export function parseLocalPaymentCatalogFromSchemaJson(
  schemaJson: string,
): LocalPaymentCatalog | null {
  const trimmed = schemaJson.trim();
  if (!trimmed) return null;
  try {
    const root = JSON.parse(trimmed) as Record<string, unknown>;
    const methodOrder = extractMethodsFromSchema(root);
    if (methodOrder.length === 0) return null;
    const { creditBrandOptions, debitBrandOptions } =
      extractCreditDebitBrandEnumsFromSchema(root);
    return {
      methodOrder,
      creditBrandOptions,
      debitBrandOptions,
    };
  } catch {
    return null;
  }
}

export function findLocalPaymentProcessedProvider(
  availableProviders: ProcessedProvider[] | null | undefined,
): ProcessedProvider | undefined {
  return availableProviders?.find((p) => p.code === ProviderCode.LocalPayment);
}

export function parseLocalPaymentCatalog(
  availableProviders: ProcessedProvider[] | null | undefined,
): LocalPaymentCatalog | null {
  const provider = findLocalPaymentProcessedProvider(availableProviders);
  if (!provider) return null;
  const schemaStr = normalizeProviderSchemaToJsonString(provider.schema);
  if (!schemaStr.trim()) return null;
  return parseLocalPaymentCatalogFromSchemaJson(schemaStr);
}

function getDefaultCatalog(): LocalPaymentCatalog {
  return {
    methodOrder: [...DEFAULT_METHOD_ORDER],
    creditBrandOptions: [...DEFAULT_CARD_BRANDS],
    debitBrandOptions: [...DEFAULT_CARD_BRANDS],
  };
}

export function resolveLocalPaymentCatalog(
  availableProviders: ProcessedProvider[] | null | undefined,
): LocalPaymentCatalog {
  return parseLocalPaymentCatalog(availableProviders) ?? getDefaultCatalog();
}

export function apiMethodValueToMethodId(apiMethod: string): string {
  const lower = apiMethod.trim().toLowerCase();
  if (lower === "credit") return MethodId.Credit;
  if (lower === "debit") return MethodId.Debit;
  if (lower === "pix") return MethodId.Pix;
  if (lower === "cash") return MethodId.Dinheiro;
  return lower.replace(/\s+/g, "_");
}

export function methodIdToApiPayloadValue(methodId: string): string {
  if (methodId === MethodId.Dinheiro) return PaymentMethod.Cash;
  if (methodId === MethodId.Credit) return PaymentMethod.Credit;
  if (methodId === MethodId.Debit) return PaymentMethod.Debit;
  if (methodId === MethodId.Pix) return PaymentMethod.Pix;
  return methodId;
}

function methodLabelPt(apiMethod: string): string {
  const match = Object.keys(METHOD_API_TO_PT).find(
    (k) => k.toLowerCase() === apiMethod.trim().toLowerCase(),
  );
  if (match) return METHOD_API_TO_PT[match];
  return apiMethod;
}

function buildBrandsFromOptions(
  optionBrands: string[],
  enabledBrands: string[],
): LocalPaymentFormMethod["brands"] {
  return optionBrands.map((brand) => ({
    id: brand.toLowerCase(),
    description: brand,
    enabled: enabledBrands.some(
      (enabled) => enabled.toLowerCase() === brand.toLowerCase(),
    ),
    imageUrl: "",
  }));
}

export function buildLocalPaymentFormMethods(
  localPaymentData: {
    methods?: string[];
    creditBrands?: string[];
    debitBrands?: string[];
  } | null,
  catalog: LocalPaymentCatalog,
): LocalPaymentFormMethod[] {
  const enabledMethods = localPaymentData?.methods ?? [];
  return catalog.methodOrder.map((apiMethod, index) => {
    const methodId = apiMethodValueToMethodId(apiMethod);
    const isEnabled = enabledMethods.some(
      (m) => m.toLowerCase() === apiMethod.trim().toLowerCase(),
    );
    let brands: LocalPaymentFormMethod["brands"] = [];
    const lower = apiMethod.trim().toLowerCase();
    if (lower === PaymentMethod.Credit.toLowerCase()) {
      brands = buildBrandsFromOptions(
        catalog.creditBrandOptions,
        isEnabled ? localPaymentData?.creditBrands ?? [] : [],
      );
    } else if (lower === PaymentMethod.Debit.toLowerCase()) {
      brands = buildBrandsFromOptions(
        catalog.debitBrandOptions,
        isEnabled ? localPaymentData?.debitBrands ?? [] : [],
      );
    }
    return {
      id: methodId,
      order: index + 1,
      description: methodLabelPt(apiMethod),
      enabled: isEnabled,
      asksFor: "",
      brands,
    };
  });
}

export function validateLocalPaymentMethods(
  formMethods: LocalPaymentFormMethod[],
): string | null {
  const invalid = formMethods.find((method) => {
    if (!method.enabled) return false;
    if (method.brands.length === 0) return false;
    return !method.brands.some((b) => b.enabled);
  });
  if (invalid) {
    return `${invalid.description}: selecione pelo menos uma bandeira.`;
  }
  return null;
}
